import { useCallback, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import {
  flipCoins,
  MAX_COIN_FLIPS,
  validateCoinFlipQuantity,
  type CoinFlipError,
  type CoinFlipResult,
} from '@/pages/coin-flipper/coin-flipper'
import { coinFlipperRoute } from '@/shared/utility-registry'

const ANALYTICS_CONTEXT = {
  app: 'calcdeck',
  domain: 'calcdeck.ru',
  route: coinFlipperRoute.path,
  utility: coinFlipperRoute.analyticsKey,
  processingMode: 'local',
} satisfies ToolAnalyticsContext
const PRESETS = [1, 3, 10, 25] as const

export function CoinFlipperPage() {
  const [quantity, setQuantity] = useState('1')
  const [result, setResult] = useState<Extract<CoinFlipResult, { status: 'success' }> | null>(null)
  const [error, setError] = useState<CoinFlipError | null>(null)

  const updateQuantity = useCallback((value: string) => {
    setQuantity(value)
    setResult(null)
    setError(null)
  }, [])

  const handleQuantity = useCallback((event: ChangeEvent<HTMLInputElement>) => updateQuantity(event.target.value), [updateQuantity])

  const flip = useCallback(() => {
    const validation = validateCoinFlipQuantity(quantity)
    if (validation.status === 'error') {
      setResult(null)
      setError(validation.error)
      trackToolRejection(ANALYTICS_CONTEXT, 'invalid_input')
      return
    }

    const startedAt = performance.now()
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const flipped = flipCoins(quantity)
    if (flipped.status === 'error') {
      setResult(null)
      setError(flipped.error)
      operation.error(flipped.error === 'processing_failed' ? 'processing_failed' : 'invalid_input')
      return
    }
    setResult(flipped)
    setError(null)
    operation.success(performance.now() - startedAt)
  }, [quantity])

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-blue-700 via-slate-400 to-blue-700" />
        <div className="grid min-w-0 lg:grid-cols-[minmax(0,0.85fr)_minmax(320px,1fr)]">
          <div className="space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div>
              <div className="mb-2 text-sm font-semibold">Быстрый выбор</div>
              <div className="grid grid-cols-4 gap-2">
                {PRESETS.map((preset) => <button key={preset} type="button" aria-pressed={quantity === String(preset)} onClick={() => updateQuantity(String(preset))} className={cn('rounded-md border px-2 py-2.5 text-sm font-semibold transition-colors', quantity === String(preset) ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:border-primary/40')}>{preset === 1 ? '1 бросок' : preset}</button>)}
              </div>
            </div>

            <label htmlFor="coin-quantity" className="block">
              <span className="mb-2 block text-sm font-semibold">Количество бросков (до {MAX_COIN_FLIPS})</span>
              <input id="coin-quantity" value={quantity} inputMode="numeric" onChange={handleQuantity} aria-invalid={error !== null} aria-describedby={error ? 'coin-error' : undefined} className={cn('h-11 w-full rounded-md border bg-background px-3 font-mono tabular-nums outline-none focus:border-primary focus:ring-2 focus:ring-ring/20', error && 'border-destructive')} />
            </label>
            {error && <div id="coin-error" role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{coinErrorText(error)}</div>}
            <Button className="w-full sm:w-auto" onClick={flip}>{quantity === '1' ? 'Подбросить монетку' : 'Запустить серию'}</Button>
          </div>

          <div className="flex min-w-0 flex-col bg-background/55 p-4 sm:p-6 lg:min-h-[360px]" aria-live="polite">
            <div className="text-sm font-medium text-muted-foreground">Результат</div>
            {result ? (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <ScoreCard label="Орёл" value={result.heads} />
                  <ScoreCard label="Решка" value={result.tails} />
                </div>
                <div className="mt-4 max-h-72 overflow-auto rounded-md border bg-card p-4">
                  <div className="mb-3 text-sm font-semibold">Последовательность</div>
                  <ol className="flex flex-wrap gap-2">
                    {result.sequence.map((side, index) => <li key={index} className={cn('rounded-md border px-3 py-2 text-sm font-semibold', side === 'heads' ? 'border-primary/35 bg-primary/10 text-primary' : 'bg-background')}>{side === 'heads' ? 'Орёл' : 'Решка'}</li>)}
                  </ol>
                </div>
              </>
            ) : (
              <div className="mt-4 rounded-md border bg-card p-4 shadow-sm"><div className="font-semibold">Один бросок или серия</div><p className="mt-2 text-sm leading-6 text-muted-foreground">Выберите количество, чтобы увидеть последовательность и общий счёт сторон.</p></div>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="До 100 бросков" text="Один бросок подходит для быстрого выбора, серия показывает распределение сторон." />
        <InfoCard title="Локально" text="Последовательность создаётся в браузере и не отправляется на сервер." />
        <InfoCard title="Без специальных гарантий" text="Инструмент не предназначен для азартных или юридически значимых решений." />
      </section>
    </div>
  )
}

function ScoreCard({ label, value }: { label: string; value: number }) {
  return <div className="rounded-lg border border-primary/30 bg-card p-4 text-center shadow-sm"><div className="text-sm text-muted-foreground">{label}</div><div className="mt-1 font-mono text-3xl font-bold tabular-nums">{value}</div></div>
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return <Card className="rounded-lg"><CardContent className="pt-6"><div className="font-semibold">{title}</div><p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p></CardContent></Card>
}

function coinErrorText(error: CoinFlipError): string {
  if (error === 'empty_quantity') return 'Укажите количество бросков.'
  if (error === 'processing_failed') return 'Не удалось получить случайные данные в браузере. Проверьте настройки браузера и попробуйте ещё раз.'
  return `Введите целое число от 1 до ${MAX_COIN_FLIPS}.`
}
